import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Divider, Paper, TextField, Typography } from "@mui/material";
import { AppColor, Fonts } from "../../../../types/Constants";
import { IndoorBeacon } from "../../../../types/models/IndoorBeacon";
import { Formik, FormikErrors, FormikHelpers, FormikTouched } from "formik";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { showMessage, updateIndoorBeacon, uploadBeaconVideo } from "../../../../redux/actions";
import { UpdateIndoorBeacon } from "../../../../types/models/UpdateIndoorBeacon";
import { getVideoURL } from "../../../../utils/utils";


interface IndoorBeaconEditorProps {
    beacon: IndoorBeacon | null
    onSubmitted: () => void;
    onClose: () => void;
}

const IndoorBeaconEditor: React.FC<IndoorBeaconEditorProps> = ({ beacon, onClose, onSubmitted }) => {

    const businessGroupState = useAppSelector(state => state.businessGroup);
    const dispatch = useAppDispatch();

    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);

    const [scrollY, setScrollY] = useState(0);

    const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const scrollToSection = (index: number) => {
        if (containerRef.current && sectionRefs.current[index]) {

            const section = sectionRefs.current[index];
            const offsetTop = section ? section.offsetTop : 0;

            containerRef.current.scrollTo({
            top: offsetTop - 175,
            behavior: "smooth",
            });
        }
    };

    const initialValues: IndoorBeacon = {
        id: beacon?.id ?? "",
        name: beacon?.name ?? "",
        description: beacon?.description ?? "",
        video_id: beacon?.video_id ?? null,
    };

    const handleScroll = () => { setScrollY(containerRef.current?.scrollTop ?? 0); }; 
    
    useEffect(() => {
        containerRef.current?.addEventListener('scroll', handleScroll);
        return () => { containerRef.current?.removeEventListener('scroll', handleScroll); };

    }, []);

    useEffect(() => {
        if (beacon?.video_id) {
            setVideoUrl(getVideoURL(beacon.video_id));
        }
    }, [beacon]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // 20mb
        if (file.size > 20 * 1024 * 1024) {
            return;
        }

        setVideoFile(file);
        setVideoUrl(URL.createObjectURL(file));
    };

    const callUpdateBeaconRequest = (beacon: IndoorBeacon, setSubmitting: (isSubmitting: boolean) => void) => {

        const beaconRequest: UpdateIndoorBeacon = {
            id: beacon.id,
            name: beacon.name,
            description: beacon.description ?? "",
            video_id: beacon.video_id ?? null,
            group_id: businessGroupState.currentIndoorExperience?.id ?? ""
        };

        dispatch(updateIndoorBeacon(beaconRequest, (result) => {
            if (result) {
                dispatch(showMessage("The Beacon has been saved"));
        
                onSubmitted();
            }
            setSubmitting(false);
        }
        ));
    };

    const handleSubmit = (values: IndoorBeacon, formikHelpers: FormikHelpers<IndoorBeacon>, ) => {
        const beacon_group_id = businessGroupState.currentIndoorExperience?.id ?? "";
        const beacon_id = values.id ?? "";

        if (videoFile == null) {
            callUpdateBeaconRequest(values, formikHelpers.setSubmitting);
        }
        else {
            // Upload video and save indoor beacon
            dispatch(uploadBeaconVideo(beacon_id, beacon_group_id, videoFile, videoFile.size, 0, 0, (videoId) => {
            if (videoId) {
                values.video_id = videoId;
                formikHelpers.setFieldValue("video_id", videoId);
                
                callUpdateBeaconRequest(values, formikHelpers.setSubmitting);
            }
            else {
                // Failed to save imageModal
                formikHelpers.setSubmitting(false);
            }
            }));
        }
    };

    const InputForm = (
        values: IndoorBeacon,
        errors: FormikErrors<IndoorBeacon>,
        handleChange: (e: React.ChangeEvent<any>) => void,
        touched: FormikTouched<IndoorBeacon>,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<IndoorBeacon>>,
        setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void
        ) => {
            return (
                <Box>
                <Paper id="section-1" ref={(el) => sectionRefs.current[0] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                    <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 1: Name</Typography>
                    <TextField
                        required
                        name="name"
                        value={values.name}
                        variant="outlined"
                        placeholder="..."
                        id="name"
                        autoComplete="off"
                        aria-autocomplete="none"
                        onChange={handleChange}
                        inputProps={{ maxLength: 40 }}
                        FormHelperTextProps={{
                        sx: {
                            textAlign: 'right',
                        },
                        }}
                        sx={{ width: "300px", p: 2 }}
                    />
                </Paper>
                <Paper id="section-2" ref={(el) => sectionRefs.current[1] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                    <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 2: Description</Typography>
                    <TextField
                        required
                        name="description"
                        value={values.description}
                        variant="outlined"
                        placeholder="..."
                        multiline
                        minRows={10}
                        size="medium"
                        fullWidth
                        id="description"
                        autoComplete="off"
                        aria-autocomplete="none"
                        onChange={handleChange}
                        inputProps={{ maxLength: 512 }}
                        FormHelperTextProps={{
                        sx: {
                            textAlign: 'right',
                        },
                        }}
                        sx={{ width: "450px", p: 2 }}
                    />
                </Paper>
                <Paper id="section-3" ref={(el) => sectionRefs.current[2] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                    <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 3: Video Upload</Typography>
                    <Box sx={{ 
                        display: "flex",
                        alignItems: "end",
                        gap: 4,
                        width: 600,
                        p: 2
                    }}>
                        {videoUrl ? (
                            <video
                            src={videoUrl}
                            controls
                            width="100%"
                            style={{
                                borderRadius: 8,
                                marginBottom: 12,
                                maxHeight: 200,
                                objectFit: "cover",
                            }}
                            />
                        ) : (
                            <Box
                            sx={{
                                width: "100%",
                                height: 200,
                                border: 'dashed 2px silver',
                                borderRadius: 2.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                                color: 'text.secondary',
                                backgroundColor: 'background.default',
                            }}
                            >
                                No video selected
                            </Box>
                        )}
                        <Box sx={{ display: "flex", gap: 2, pb: 2 }}>
                            <Button variant="outlined" component="label" sx={{ whiteSpace: "nowrap" }}>
                            Choose Video
                            <input
                                type="file"
                                accept="video/*"
                                hidden
                                onChange={handleFileChange}
                            />
                            </Button>
                        </Box>
                    </Box>
                </Paper>
                </Box>
            );
        };

    return (
    <Formik
        enableReinitialize
        validateOnChange={true}
        initialValues={initialValues}
        onSubmit={handleSubmit}
    >
        {({
        values,
        errors,
        touched,
        isSubmitting,
        handleChange,
        submitForm,
        resetForm,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        }) => (
        <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
        }}
        >
            <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 4,
                width: "100%"
            }}
            >
                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap"}}>
                    <Button
                    sx={{
                        color: AppColor.darkBrand, 
                        fontSize: 16, 
                        fontWeight: Fonts.SEMI_BOLD,
                        textTransform: "none",
                        padding: 0,
                        textDecoration: scrollY < 149 ? "underline" : "none",
                        "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                        flexShrink: 0
                    }}
                    onClick={() => scrollToSection(0)}
                    >
                    Step 1: Name
                    </Button>
                    <Button
                    sx={{
                        color: AppColor.darkBrand, 
                        fontSize: 16, 
                        fontWeight: Fonts.SEMI_BOLD,
                        textTransform: "none",
                        padding: 0,
                        textDecoration: scrollY >= 149 && scrollY <= 225 ? "underline" : "none",
                        "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                        flexShrink: 0
                    }}
                    onClick={() => scrollToSection(1)}
                    >
                    Step 2: Description
                    </Button>
                    <Button
                    sx={{
                        color: AppColor.darkBrand, 
                        fontSize: 16, 
                        fontWeight: Fonts.SEMI_BOLD,
                        textTransform: "none",
                        padding: 0,
                        textDecoration: scrollY > 225 ? "underline" : "none",
                        "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                        flexShrink: 0
                    }}
                    onClick={() => scrollToSection(2)}
                    >
                    Step 3: Video Upload
                    </Button>
                </Box>
                <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    flexShrink: 0,
                    alignSelf: "flex-end",
                    paddingBottom: "16px",
                }}
                >
                {/* Close / Cancel */}
                <Button
                    variant="outlined"
                    sx={{ marginRight: 4 }}
                    onClick={() => {
                        resetForm();
                        onClose();
                    }}
                >
                    Cancel
                </Button>

                {/* Save */}
                <Button
                    sx={{ marginRight: 2, flexShrink: 0 }}
                    variant="contained"
                    onClick={submitForm}
                    disabled={isSubmitting}
                >
                    Save
                </Button>
                </Box>
            </Box>
            <Divider />
            <Box 
            ref={containerRef} 
            sx={{ 
                width: '100%', 
                maxHeight: "calc(100vh - 185px)", 
                overflowY: "auto", 
                paddingTop: 1 
            }}
            >
                {InputForm(
                    values,
                    errors,
                    handleChange,
                    touched,
                    setFieldValue,
                    setFieldTouched,
                )}
            </Box>
        </Box>
        )}
    </Formik>
    );
};


export default IndoorBeaconEditor;